import { css } from '@emotion/react'
import styled from '@emotion/styled'
import { Image, Props as ImageProps } from 'app/components/Common/Image'
import { theme } from 'app/theme'
import { rgba } from 'emotion-rgba'
import { Link } from 'gatsby'
import React, { memo } from 'react'
import { LazyLoadComponent } from 'react-lazy-load-image-component'

export interface Props {
  className?: string
  category?: string
  image?: ImageProps
  price?: string
  subtitle?: string
  title: string
  URL: string
  variant?: Variant
}

export const Slide = memo(function Slide({
  category,
  className,
  image,
  price,
  subtitle,
  title,
  URL,
  variant = 'default',
}: Props) {
  if (!title || !URL) {
    return null
  }

  return (
    <Container className={className} to={URL} variant={variant}>
      <Background className="bg">
        {image ? (
          <LazyLoadComponent>
            <Image {...image} />
          </LazyLoadComponent>
        ) : null}

        {category ? <Category>{category}</Category> : null}
      </Background>

      <Wrapper>
        <Title>{title}</Title>

        {subtitle ? <Subtitle>{subtitle}</Subtitle> : null}

        {price ? <Price>{price}</Price> : null}
      </Wrapper>
    </Container>
  )
})

const Background = styled.div`
  height: 27.1vw;
  overflow: hidden;
  position: relative;
  &:before {
    content: '';
    width: 100%;
    height: 50%;
    background: linear-gradient(
      ${rgba(theme.colors.variants.neutralDark1, 0.5)},
      ${rgba(theme.colors.variants.neutralDark1, 0)}
    );
    position: absolute;
    top: 0;
    left: 0;
    z-index: 2;
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    position: absolute;
    top: 0;
    left: 0;
    transition: 0.3s ease-in-out;
  }

  @media (max-width: 1199px) {
    aspect-ratio: 1.5;
    height: auto;
  }
`

const Category = styled.div`
  background: ${rgba(theme.colors.variants.neutralLight5, 0.2)};
  border-radius: 0.6875rem;
  color: ${({ theme }) => theme.colors.variants.neutralLight5};
  font-family: ${({ theme }) => theme.fontFamily.heading};
  font-size: 0.5625rem;
  font-weight: 500;
  letter-spacing: 0.15em;
  line-height: 0.6875rem;
  padding: 0.375rem 0.5625rem;
  position: absolute;
  top: 1rem;
  right: 1rem;
  text-transform: uppercase;
  transition: 0.3s ease-in-out;
  z-index: 2;

  @media (max-width: 1199px) {
    background: ${({ theme }) => theme.colors.variants.primaryLight};
    color: ${({ theme }) => theme.colors.variants.primaryDark};
  }
`

const Wrapper = styled.div`
  margin-top: 2.25rem;

  @media (max-width: 1199px) {
    margin-top: 1.5rem;
  }
`

const Title = styled.div`
  color: ${({ theme }) => theme.colors.variants.primaryDark};
  font-family: ${({ theme }) => theme.fontFamily.heading};
  font-size: 1.5rem;
  font-weight: 500;
  line-height: 1.5rem;

  @media (max-width: 1199px) {
    font-size: 1.25rem;
    line-height: 1.25rem;
  }
`

const Subtitle = styled.div`
  color: ${({ theme }) => theme.colors.variants.neutralDark3};
  font-family: ${({ theme }) => theme.fontFamily.paragraph};
  font-size: 1rem;
  font-weight: 300;
  line-height: 1.375rem;
  margin-top: 0.375rem;
`

const Price = styled.div`
  color: ${({ theme }) => theme.colors.variants.primaryDark};
  font-family: ${({ theme }) => theme.fontFamily.heading};
  font-size: 0.75rem;
  font-weight: 500;
  letter-spacing: 0.15em;
  line-height: 1rem;
  margin-top: 1.5rem;
  padding-left: 3.125rem;
  position: relative;
  text-transform: uppercase;
  &:before,
  &:after {
    content: '';
    height: 0.0625rem;
    position: absolute;
    top: 0.5rem;
    left: 0;
  }
  &:before {
    width: 2.25rem;
    background: ${({ theme }) => theme.colors.variants.neutralLight2};
  }
  &:after {
    width: 0;
    background: ${({ theme }) => theme.colors.variants.primaryLight};
    transition: 0.3s ease-in-out;
  }

  @media (max-width: 1199px) {
    &:before {
      width: 1.875rem;
    }
  }
`

const Container = styled(Link)<ContainerProps>`
  display: block;
  text-align: left;
  &:hover {
    ${Background} {
      img {
        transform: scale(1.1) translate3d(-0.0625rem, -0.0625rem, 0);
      }
    }
    ${Category} {
      background: ${({ theme }) => theme.colors.variants.primaryLight};
    }
    ${Price} {
      &:after {
        width: 2.25rem;
      }
    }
  }

  ${({ theme, variant }) => {
    switch (variant) {
      case 'light':
        return css`
          ${Title},
          ${Subtitle},
          ${Price} {
            color: ${theme.colors.variants.neutralLight5};
          }
        `
    }
  }}

  @media (max-width: 1199px) {
    &:hover {
      ${Price} {
        &:after {
          width: 1.875rem;
        }
      }
    }
  }
`

interface ContainerProps {
  variant: Variant
}

export type Variant = 'default' | 'light'
